import { useTranslation, type TranslationKeys } from "@/hooks/useTranslation/useTranslation"
import clsx from "clsx"
import { useStore } from "../../store/useStore"
import type { RelationTag as RelationTagType } from "../../types"

const SCOPE_COLORS: Record<string, string> = {
  supplier: "bg-purple1/25 text-[#c084fc] hover:bg-purple1/35",
  component: "bg-yellow1/15 text-yellow3 hover:bg-yellow1/25",
  product: "bg-success/20 text-success hover:bg-success/30",
  customer: "bg-warning/20 text-warning hover:bg-warning/30",
  substance: "bg-red1/25 text-[#ff9a8d] hover:bg-red1/35",
}

const FRESHNESS_STYLES = {
  fresh: {
    bg: "bg-success/15",
    border: "ring-1 ring-inset ring-success/30",
    text: "text-success",
  },
  stale: {
    bg: "bg-yellow1/15",
    border: "ring-1 ring-inset ring-yellow1/30",
    text: "text-yellow1",
  },
  empty: {
    bg: "bg-red1/15",
    border: "ring-1 ring-inset ring-red1/30",
    text: "text-red1",
  },
} as const

function getFreshness(
  timestamp: string | null | undefined,
  freshnessDate: string | null,
): "fresh" | "stale" | "empty" {
  if (!timestamp) return "empty"
  if (!freshnessDate) return "fresh"
  return timestamp.slice(0, 10) >= freshnessDate ? "fresh" : "stale"
}

function formatRelativeTime(iso: string, translate: (key: TranslationKeys, params?: Record<string, string | number>) => string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return translate("QMS/COMMON/JUST_NOW")
  if (mins < 60) return translate("QMS/COMMON/MINS_AGO", { count: mins })
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return translate("QMS/COMMON/HOURS_AGO", { count: hrs })
  const days = Math.floor(hrs / 24)
  if (days < 14) return translate("QMS/COMMON/DAYS_AGO", { count: days })
  const weeks = Math.floor(days / 7)
  if (weeks < 8) return translate("QMS/COMMON/WEEKS_AGO", { count: weeks })
  const months = Math.floor(days / 30)
  return translate("QMS/COMMON/MONTHS_AGO", { count: months })
}

interface Props {
  tag: RelationTagType | undefined
  primTitle: string
  levelLabel?: string
  relatedScopeId: string
  onClick: () => void
  active?: boolean
  /** Earliest Record.updated_at among ALL upstream records reachable through this
   *  relation (including transitive hops). Used for cascade freshness — if any
   *  upstream record is stale, this tag shows yellow even when direct links are fresh. */
  cascadeEarliestTimestamp?: string | null
}

export function RelationTag({
  tag,
  primTitle,
  levelLabel,
  relatedScopeId,
  onClick,
  active,
  cascadeEarliestTimestamp,
}: Props) {
  const count = tag?.count ?? 0
  const { translate } = useTranslation()
  const color =
    SCOPE_COLORS[relatedScopeId] ?? "bg-grey4/40 text-grey5 hover:bg-grey4/60"
  const isDiffMode = useStore(
    (s) =>
      s.actionState !== null && Object.keys(s.actionState.diffs).length > 0,
  )
  const freshnessDate = useStore((s) => s.freshnessDate)
  // Use the earliest of direct relation timestamp and cascade (transitive) timestamp
  const directTs = tag?.earliest_updated_at ?? null
  const effectiveTs =
    cascadeEarliestTimestamp && directTs
      ? cascadeEarliestTimestamp < directTs
        ? cascadeEarliestTimestamp
        : directTs
      : (cascadeEarliestTimestamp ?? directTs)
  const freshness =
    isDiffMode && count > 0 ? getFreshness(effectiveTs, freshnessDate) : null
  const fStyles = freshness ? FRESHNESS_STYLES[freshness] : null

  return (
    <div className="flex flex-col items-start gap-0.5">
      {freshness && (
        <span className={clsx("text-[9px] leading-tight", fStyles!.text)}>
          {freshness === "empty"
            ? translate("QMS/PIVOT/NO_DATE")
            : translate("QMS/PIVOT/OLDEST_LABEL", {
                ts: formatRelativeTime(effectiveTs!, translate),
              })}
        </span>
      )}
      <button
        onClick={onClick}
        className={clsx(
          "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium transition-colors",
          color,
          active && "ring-2 ring-current ring-offset-1 ring-offset-grey2",
          fStyles && `${fStyles.bg} ${fStyles.border}`,
        )}
      >
        <span>{count}</span>
        <span className="opacity-70">{levelLabel ?? primTitle}</span>
        <span className="opacity-50">{active ? "↓" : "→"}</span>
      </button>
    </div>
  )
}
